import { isPublicWebPath, pickT } from "@repo/shared";
import { type ReactNode, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { SsoProviderChangeBanner } from "@/components/auth/sso-provider-change-banner";
import { CloneJobNotifier } from "@/components/clone-jobs";
import { Navigation } from "@/components/navigation";
import { ReleaseNotesModal } from "@/components/release-notes-modal";
import { Sidebar } from "@/components/sidebar/sidebar";
import { SidebarLayout } from "@/components/sidebar/sidebar-layout";
import { getSessionTenantSlug, useSession } from "@/lib/auth-client";
import { BrandColorProvider } from "@/lib/context/brand-color-context";
import { SidebarProvider } from "@/lib/context/sidebar-context";
import { installBrowserDiagnostics } from "@/lib/diagnostics/browser-diagnostics";
import { useAppBootstrap } from "@/lib/hooks/use-app-bootstrap";
import { presenceHeartbeatEnabled, usePresenceHeartbeat } from "@/lib/hooks/use-notifications";
import { useStandaloneMode } from "@/lib/hooks/use-standalone-mode";
import { UserLanguageProvider } from "@/lib/i18n/context";
import { detectBrowserLanguage } from "@/lib/i18n/detect-browser-language";
import { hasPermission, type Permission } from "@/lib/permissions";
import { RegionalFormatProvider } from "@/lib/regional-format/context";
import { getTenantHostContext } from "@/lib/tenant-host";
import { hexToHSL } from "@/lib/utils/theme-colors";
import { T } from "./app-shell.i18n";

// Install console/network diagnostics ring buffers as early as possible so a
// support ticket can attach the last events that preceded the user's report.
installBrowserDiagnostics();

export function AppShell({ children }: { children: ReactNode }) {
  const { data: session, isPending: sessionLoading } = useSession();
  const {
    data: bootstrap,
    isLoading: bootstrapLoading,
    isFetching: bootstrapFetching,
    isError,
  } = useAppBootstrap();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // QL-33 — standalone flow-instance links render without the app chrome.
  const { standalone } = useStandaloneMode();
  const hostContext = useMemo(getTenantHostContext, []);
  const sessionTenantSlug = getSessionTenantSlug(session);
  // A session without a tenant slug counts as foreign on a tenant host too:
  // AuthGuard signs it out instead of rendering an unverified organization.
  const isForeignHost =
    !!session &&
    (hostContext.kind === "root" ||
      (hostContext.kind === "tenant" && sessionTenantSlug !== hostContext.slug));

  // QL-728 — "QP is open" while a signed-in app tab with the bell is visible: the
  // advisor's notifications then show here instead of being pushed to the phone.
  usePresenceHeartbeat(
    presenceHeartbeatEnabled({
      signedIn: !!session && !!bootstrap,
      foreignHost: isForeignHost,
      publicPage: isPublicWebPath(pathname),
      standalone,
    }),
  );

  // Inject tenant brand color as --brand so the nav underline and card hover
  // use the tenant's color instead of the hardcoded orange default.
  useEffect(() => {
    const color = bootstrap?.theme?.themePrimaryColor;
    const root = document.documentElement;
    if (color) {
      try {
        root.style.setProperty("--brand", hexToHSL(color));
      } catch {
        root.style.removeProperty("--brand");
      }
    } else {
      root.style.removeProperty("--brand");
    }
  }, [bootstrap?.theme?.themePrimaryColor]);

  // Stale session detection: cookie exists but session is invalid
  useEffect(() => {
    if (sessionLoading || session || isPublicWebPath(pathname)) return;

    // Check if session cookie exists (non-HttpOnly portion check)
    // The middleware already handles redirect to /login for unauthenticated users,
    // but if we get here with no session on a protected path, redirect to /clear
    // to clean up any stale cookies
    const hasCookie = document.cookie.includes("qp.session_token");
    if (hasCookie) {
      // QL-261 — keep the requested URL (path AND query, e.g. a standalone
      // launch link) so /clear can forward it to /login?redirect= and the
      // user lands where they were headed after signing in again.
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      navigate(returnTo === "/" ? "/clear" : `/clear?redirect=${encodeURIComponent(returnTo)}`, {
        replace: true,
      });
    }
  }, [sessionLoading, session, pathname, navigate]);

  // Public paths (oauth, etc.) — bare layout regardless of session state.
  // QL-343 — likewise a session on the root domain or on another tenant's host:
  // AuthGuard / the login page redirect or show the wrong-organization overlay,
  // which must not wait for (or depend on) that session's app bootstrap.
  if (isPublicWebPath(pathname) || isForeignHost) {
    return <>{children}</>;
  }

  // Not logged in — bare layout for login, clear, public pages
  if (!sessionLoading && !session) {
    return <main className="container mx-auto px-4 py-8">{children}</main>;
  }

  // Loading session or bootstrap data. QL-628 — a signed-out `null` in the cache
  // (cold load or sign-out in this tab) is refetched once a session appears; it
  // has data, so isLoading is false — wait for that refetch instead of showing
  // the error screen below.
  if (
    sessionLoading ||
    (session && (bootstrapLoading || (bootstrap === null && bootstrapFetching)))
  ) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Bootstrap failed — show error with retry
  if (isError || !bootstrap) {
    const t = pickT(T, detectBrowserLanguage());
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">{t.loadFailed}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm text-primary hover:underline"
          >
            {t.tryAgain}
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrandColorProvider color={bootstrap.theme?.themePrimaryColor}>
      <UserLanguageProvider
        userLanguage={bootstrap.language.userLanguage}
        tenantDefaultLanguage={bootstrap.language.tenantDefaultLanguage}
        tenantSupportedLanguages={bootstrap.language.tenantSupportedLanguages}
      >
        <RegionalFormatProvider regionalFormat={bootstrap.regionalFormat}>
          <SidebarProvider>
            {standalone ? (
              // QL-33 standalone mode: no top navigation, no sidebar rail (and no
              // SidebarLayout, whose margin reserves space for it), no release-notes
              // modal. SidebarProvider stays mounted — pages call useSidebar() for
              // support context, and DocButton disables itself in standalone mode.
              <main className="px-4 py-8 max-w-7xl mx-auto">{children}</main>
            ) : (
              <>
                <SidebarLayout>
                  <Navigation
                    user={bootstrap.user}
                    logoLightUrl={bootstrap.theme?.logoLightUrl}
                    logoDarkUrl={bootstrap.theme?.logoDarkUrl}
                    isPartner={bootstrap.isPartner}
                  />
                  <main className="px-4 py-8 max-w-7xl mx-auto">
                    <SsoProviderChangeBanner
                      change={bootstrap.ssoProviderChange}
                      permissions={bootstrap.user.permissions}
                    />
                    {children}
                  </main>
                </SidebarLayout>
                <Sidebar chartColors={bootstrap.theme?.chartColors} />
                <ReleaseNotesModal />
                <CloneJobNotifier
                  enabled={hasPermission(bootstrap.user.permissions as Permission[], "flows")}
                />
              </>
            )}
          </SidebarProvider>
        </RegionalFormatProvider>
      </UserLanguageProvider>
    </BrandColorProvider>
  );
}
